<template>
  <v-card>
    <v-card-text>
      <v-row no-dense>
        <v-col cols="4">
          <v-autocomplete
            v-model="selectedCategories"
            :items="categoryFilters"
            label="Categories"
            chips
            small-chips
            dense
            filled
            multiple
            hide-details
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            v-model="selectedReactions"
            :items="reactionFilters"
            label="Reactions"
            chips
            small-chips
            dense
            filled
            multiple
            hide-details
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            v-model="selectedUsers"
            :items="userFilters"
            label="Users"
            chips
            small-chips
            dense
            filled
            multiple
            hide-details
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            v-model="selectAction"
            :items="actions"
            label="Actions"
            chips
            small-chips
            item-value="name"
            item-text="name"
            dense
            filled
            multiple
            hide-details
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            v-model="selectAssignee"
            :items="users"
            label="Assignee"
            chips
            small-chips
            dense
            filled
            multiple
            hide-details
          />
        </v-col>
      </v-row>
      <div>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </div>
    </v-card-text>
    <v-data-table
      :headers="headers"
      :items="filteredFeedback"
      :search="search"
      :loading="loading"
      :items-per-page="50"
      :footer-props="{
        'items-per-page-options': [50, 100, 250, -1]
      }"
      multi-sort
    >
      <template v-slot:item.screenshot="{ item }">
        <v-dialog
          v-model="item.dialog"
          width="1024"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-img
              v-on="on"
              v-bind="attrs"
              max-height="50"
              max-width="150"
              contain
              :src="getImageSrc(item.screenshot)"
            />
          </template>
          <v-img
            width="1024"
            contain
            :src="getImageSrc(item.screenshot)"
          />
        </v-dialog>
      </template>
      <template v-slot:item.reaction="{ item }">
        <v-chip
          small
          :color="colors[item.reaction].color"
          :dark="colors[item.reaction].dark"
        >
          <b>{{ item.reaction }}</b>
        </v-chip>
      </template>
      <template v-slot:item.PlayerNickname="{ item }">
        <div class="caption">
          User: {{ item.PlayerNickname }}
        </div>
        <div class="caption">
          UserID: {{ item.UserID }}
        </div>
        <div>
          <v-menu
            offset-y
            @input="(event) => loadPlayerHardware(event, item.session_id)"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                x-small
                outlined
                :color="item.lowHardware ? 'red' : 'black'"
                v-bind="attrs"
                v-on="on"
              >
                Hardware
                <v-icon
                  v-if="item.lowHardware"
                  right
                  x-small
                >
                  {{ mdiLaptopOff }}
                </v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-text v-if="hardwareLoading" class="text-center">
                <v-progress-circular
                  :size="50"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </v-card-text>
              <v-card-text v-else>
                <table>
                  <tbody>
                    <tr v-for="row in hardware" :key="row.name">
                      <td class="px-1">
                        {{ row.name }}
                      </td>
                      <td class="px-1">
                        {{ row.value }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </template>
      <template v-slot:item.coordinates="{ item }">
        <div class="caption">
          {{ item.map.map_name }}
        </div>
        <div class="caption">
          Floor: {{ item.map.map_floor }}
        </div>
        <div class="caption">
          Playtime: {{ timeToString(item.playtime) }}
        </div>
        <v-btn
          x-small
          outlined
          @click="copyToClipboard(item.coordinates)"
        >
          copy coordinates
        </v-btn>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <div class="flex-grow-1">
            <v-select
              v-model="item.details.call_to_action"
              :items="actions"
              label="Call to action"
              class="my-1"
              outlined
              item-text="name"
              item-value="name"
              dense
              hide-details
              :disabled="selectAction.length > 0"
            />
            <v-autocomplete
              v-model="item.details.assignee"
              :items="users"
              label="Assignee"
              class="my-1"
              outlined
              dense
              hide-details
              :disabled="selectAssignee.length > 0"
            />
            <v-textarea
              v-model="item.details.comment"
              label="Comment"
              class="my-1"
              outlined
              auto-grow
              rows="1"
              dense
              hide-details
            />
          </div>
          <v-btn
            class="mx-1"
            icon
            @click="saveDetails(item)"
          >
            <v-icon>{{ mdiContentSave }}</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mdiMagnify, mdiContentSave, mdiLaptopOff } from '@mdi/js'
import helpers from '../../plugins/helpers/mixin-helper.js'
export default {
  mixins: [
    helpers
  ],
  data () {
    return {
      mdiMagnify,
      mdiContentSave,
      mdiLaptopOff,
      search: '',
      loading: false,
      feedback: [],
      hardware: [],
      hardwareLoading: false,
      colors: {
        'Very Bad': {
          color: '#F44336',
          dark: true
        },
        'Bad': {
          color:'#FF9800',
          dark: true
        },
        'Good': {
          color:'#FFEB3B',
          dark: false
        },
        'Very Good': {
          color:'#4CAF50',
          dark: true
        },
      },
      headers: [
        {
          text: 'Screenshot',
          align: 'center',
          filterable: false,
          sortable: false,
          value: 'screenshot',
          width: 100
        },
        {
          text: 'Category',
          align: 'left',
          value: 'category',
        },
        {
          text: 'Reaction',
          align: 'center',
          value: 'reaction',
          width: 100
        },
        {
          text: 'User',
          align: 'left',
          value: 'PlayerNickname',
          width: 200
        },
        {
          text: 'Coordinates',
          align: 'left',
          sortable: false,
          filterable: false,
          value: 'coordinates',
          wisth: 250
        },
        {
          text: 'Feedback',
          value: 'feedback',
          sortable: false,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: 300
        }
      ],
      selectedCategories: [],
      selectedReactions: [],
      selectedUsers: [],
      selectAction: [],
      selectAssignee: [],
      users: [],
      actions: []
    }
  },
  async mounted () {
    this.$eventBus.$on('feedback-chart-data-point-selected', ({ reaction, category }) => {
      this.selectedCategories = [category]
      this.selectedReactions = [reaction]
    })

    await Promise.all([
      this.getStatistic(),
      this.getUsers(),
      this.getActions()
    ])
  },
  beforeDestroy () {
    this.$eventBus.$off('feedback-chart-data-point-selected')
  },
  watch: {
    async '$route.query' () {
      await this.getStatistic()
    }
  },
  computed: {
    filteredFeedback () {
      return this.feedback.filter(feedback => {
        let flag = true

        if (this.selectedCategories.length) {
          flag = flag && this.selectedCategories.includes(feedback.category)
        }

        if (this.selectedReactions.length) {
          flag = flag && this.selectedReactions.includes(feedback.reaction)
        }

        if (this.selectedUsers.length) {
          flag = flag && this.selectedUsers.includes(feedback.PlayerNickname)
        }

        if (this.selectAction.length) {
          flag = flag && this.selectAction.includes(feedback.details.call_to_action)
        }

        if (this.selectAssignee.length) {
          flag = flag && this.selectAssignee.includes(feedback.details.assignee)
        }

        return flag
      })
    },
    categoryFilters () {
      return this.feedback.reduce((categories, feedback) => {
        if (categories.includes(feedback.category)) return categories
        return [...categories, feedback.category]
      }, [])
    },
    reactionFilters () {
      return this.feedback.reduce((reactions, feedback) => {
        if (reactions.includes(feedback.reaction)) return reactions
        return [...reactions, feedback.reaction]
      }, [])
    },
    userFilters () {
      return this.feedback.reduce((users, feedback) => {
        if (users.includes(feedback.PlayerNickname)) return users
        return [...users, feedback.PlayerNickname]
      }, [])
    }
  },
  methods: {
    async getUsers () {
      const { data } = await this.$axios.get('/api/users')
      .catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.users = data
      }
    },
    async getActions () {
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/feedback/actions`
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.actions = data
      }
    },
    async getStatistic () {
      if (!this.$route.query.build_id) {
        return
      }

      this.loading = true
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/telemetry/feedback-data`, {
          params: {
            build_id: this.$route.query.build_id
          }
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.feedback = data
      }
      this.loading = false
    },
    getImageSrc (name) {
      return this.$store.getters.projectPath + '/feedback/' + name
    },
    async saveDetails (item) {
      const { data } = await this.$axios.put(
        `${this.$store.getters.projectPath}/telemetry/feedback-data/${item._id}`, {
          ...item.details
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.$toasted.success('Feedback details updated')
      }
    },
    async loadPlayerHardware (event, session_id) {
      if (!event) {
        this.$nextTick(() => {
          this.hardware = []
          this.hardwareLoading = false
        })
        return
      }

      this.hardwareLoading = true
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/telemetry/hardware/session/${session_id}`
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.hardware = Object.keys(data).map(key => ({
          name: key,
          value: Array.isArray(data[key])
            ? data[key].reduce((acc, val) => acc + "; " + val)
            : data[key]
        }))
      }
      this.hardwareLoading = false
    }
  }
}
</script>